import AirOutlinedIcon from "@mui/icons-material/AirOutlined";
import Co2OutlinedIcon from "@mui/icons-material/Co2Outlined";
import CropFreeOutlinedIcon from "@mui/icons-material/CropFreeOutlined";
import DeviceThermostatOutlinedIcon from "@mui/icons-material/DeviceThermostatOutlined";
import GrassOutlinedIcon from "@mui/icons-material/GrassOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import WaterOutlinedIcon from "@mui/icons-material/WaterOutlined";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import FlashOnOutlinedIcon from "@mui/icons-material/FlashOnOutlined";
import PowerSettingsNewOutlinedIcon from "@mui/icons-material/PowerSettingsNewOutlined";
import _ from "lodash";

// Mediciones que la tarjeta resume de un vistazo. El detalle completo de cada variable vive en el panel, que recibe
// del backend su etiqueta, unidad y decimales. onlyWithValue: se muestra solo si viene en la última lectura; title:
// explica el valor al pasar el mouse.
export const CARD_MEASUREMENTS = {
    temperature: { icon: DeviceThermostatOutlinedIcon, color: "climate", unit: "°C", decimals: 1 },
    humidity: { icon: WaterDropOutlinedIcon, color: "climate", unit: "%", decimals: 0 },
    pressure: { icon: SpeedOutlinedIcon, color: "climate", unit: "hPa", decimals: 0 },
    co2: { icon: Co2OutlinedIcon, color: "success", unit: "ppm", decimals: 0 },
    illuminance: { icon: LightModeOutlinedIcon, color: "warning", unit: "lux", decimals: 0 },
    energy: { icon: FlashOnOutlinedIcon, color: "energy", unit: "kWh", decimals: 1 },
    state: { icon: PowerSettingsNewOutlinedIcon, color: "energy", unit: "", decimals: -1 },
    // Conteos de Xpit AI Vision: un punto de línea reporta entradas y salidas, y uno de zona cuántos hay dentro.
    count_present: { icon: CropFreeOutlinedIcon, color: "primary", unit: "", decimals: 0, onlyWithValue: true, title: "Dentro de la zona" },
    count_in: { icon: LoginOutlinedIcon, color: "primary", unit: "", decimals: 0, onlyWithValue: true, title: "Entradas del último intervalo" },
    count_out: { icon: LogoutOutlinedIcon, color: "primary", unit: "", decimals: 0, onlyWithValue: true, title: "Salidas del último intervalo" },
};

// Las categorías de DeviceType, con nombre en español. Un tipo sin categoría llega del backend como "other".
export const DEVICE_CATEGORIES = {
    climate: { label: "Clima", icon: DeviceThermostatOutlinedIcon, color: "climate" },
    energy: { label: "Energía", icon: FlashOnOutlinedIcon, color: "energy" },
    motion: { label: "Personas", icon: GroupsOutlinedIcon, color: "primary" },
    water: { label: "Agua", icon: WaterOutlinedIcon, color: "info" },
    air_quality: { label: "Aire", icon: AirOutlinedIcon, color: "success" },
    agriculture: { label: "Agro", icon: GrassOutlinedIcon, color: "success" },
    location: { label: "Ubicación", icon: PlaceOutlinedIcon, color: "error" },
    security: { label: "Seguridad", icon: ShieldOutlinedIcon, color: "secondary" },
    // Puntos de los Jetson de Xpit AI Vision: qué cuentan lo dice cada analítica (personas, vehículos…).
    vision: { label: "Visión", icon: VideocamOutlinedIcon, color: "primary" },
    other: { label: "Otros", icon: SensorsOutlinedIcon, color: "energy_off" },
};

// Solo las categorías que el local tiene, de la más numerosa a la menos. Una categoría desconocida igual se muestra,
// con la presentación de "Otros", para que un tipo nuevo no haga desaparecer dispositivos de la cuenta.
export const categorySummary = (categories) => (
    _.chain(categories)
        .toPairs()
        .filter(([, quantity]) => quantity > 0)
        .map(([key, quantity]) => ({
            key,
            quantity,
            ...(DEVICE_CATEGORIES[key] || DEVICE_CATEGORIES.other),
        }))
        .orderBy(["quantity", "label"], ["desc", "asc"])
        .value()
);

// Mismo umbral con que el motor de alertas descarta una lectura por vieja: si el sensor no reporta hace más de esto,
// lo que se muestra ya no representa su estado actual.
export const STALE_READING_MINUTES = 30;

export const formatMeasurementValue = (value, { unit = "", decimals = 1 } = {}) => {
    if (_.isNil(value) || value === "") return "-";
    if (decimals < 0 || !_.isFinite(Number(value))) return `${value}${unit ? ` ${unit}` : ""}`;

    const rounded = _.round(Number(value), decimals).toFixed(Math.max(decimals, 0));
    return unit ? `${rounded} ${unit}` : rounded;
};

// "hace 3 min", "hace 2 h", "hace 4 d". Sin librería: es una sola frase y moment ya pesa demasiado en el bundle.
export const formatTimeAgo = (isoTime) => {
    if (!isoTime) return null;
    const time = new Date(isoTime);
    if (Number.isNaN(time.getTime())) return null;

    const minutes = Math.floor((Date.now() - time.getTime()) / 60000);
    if (minutes < 1) return "recién";
    if (minutes < 60) return `hace ${minutes} min`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `hace ${hours} h`;
    return `hace ${Math.floor(hours / 24)} d`;
};

export const isReadingStale = (isoTime) => {
    if (!isoTime) return true;
    const time = new Date(isoTime);
    if (Number.isNaN(time.getTime())) return true;

    return (Date.now() - time.getTime()) / 60000 > STALE_READING_MINUTES;
};

export const deviceMeasuresField = (device, field) => (
    !!_.find(device?.device_type?.measurement_types, (measurementType) => measurementType?.name === field)
);
